import { Ban, CheckCircle2, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { FamilyRelationModal } from '../components/FamilyRelationModal';
import { familyRelationsApi } from '../familyRelationsApi';
import type { FamilyRelation } from '../familyRelationTypes';

type TabKey = 'info';

/**
 * One family relation.
 *
 * On the **header** layout rather than the sidebar one: a catalog row reads as a single
 * continuous thing — a name, what the form does with it, and the trail of who changed it — not
 * a record whose content splits into panels a reader chooses between.
 *
 * It earns its keep on the count: how many family members are already recorded at a relation is
 * what a reader wants before renaming it, deleting it, or narrowing it to one per record.
 */
export function FamilyRelationProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [relation, setRelation] = useState<FamilyRelation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      familyRelationsApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) setRelation(next);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this relation.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  // A plain string is a segment that goes nowhere; the two links are the way back.
  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'Personnel',
    { label: 'Settings', to: '/personnel/settings' },
    { label: 'Family Relations', to: '/personnel/settings/family-relations' },
    relation?.name ?? 'Relation',
  ];

  if (error) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !relation) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="family-relation-profile"
        header={
          <ProfileHeader
            id="family-relation-profile-header"
            icon={<Users className="h-6 w-6" aria-hidden="true" />}
            title={relation.name}
            meta={[
              // Unlabelled, because "12 members" says what it is on its own.
              `${relation.recordCount} ${relation.recordCount === 1 ? 'member' : 'members'}`,
            ]}
            status={
              <StatusText tone={relation.isActive ? 'active' : 'inactive'}>
                {relation.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={relation.createdByName}
            createdAt={relation.createdAt}
          />
        }
        banner={
          relation.isActive ? (
            <ProfileBanner
              id="family-relation-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This relation is offered: it appears in the Relation picker on every personnel record's family form."
            />
          ) : (
            <ProfileBanner
              id="family-relation-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This relation is deleted. It is no longer offered, and the family members already recorded against it are kept."
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="family-relation-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'family-relations.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: relation.name },
              { label: 'Description', value: relation.description },
              { label: 'Form guidance', value: relation.guidance },
              {
                label: 'Order',
                value: <span className="font-mono tabular-nums">{relation.sortOrder}</span>,
              },
              {
                label: 'Members recorded',
                value: <span className="font-mono tabular-nums">{relation.recordCount}</span>,
              },

              /*
                The three flags live on THIS card rather than a second one, and that is a rule
                rather than a layout preference.

                They shipped as their own "What the family form does" card for an hour, which
                read well and broke two rules at once: every card of facts owes a cog with an
                Edit in it — the only exemptions are Audit, a card moved by an action, and a
                card over another module's record, and this was none of them — and one card is
                one modal, so giving it a second Edit into the SAME modal would have been the
                other half of the same mistake.

                The seam it was split along was ours, not the reader's: they came to correct
                what the row does, and which of those corrections is a name and which is a
                checkbox is our storage layout, not their question.
              */
              {
                label: 'Several per record',
                value: relation.allowsMultiple
                  ? 'Allowed — a record can hold any number at this relation'
                  : 'One only — a second is refused',
              },
              {
                label: 'Date of birth',
                value: relation.requiresDateOfBirth
                  ? 'Required — the form refuses a member without one'
                  : 'Optional',
              },
              {
                label: 'Occupational info',
                value: relation.collectsOccupation
                  ? 'Asked — occupation, employer, business address and telephone'
                  : 'Not asked',
              },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="family-relation-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: relation.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(relation.createdAt)} className="font-mono">
                    {formatDateTime(relation.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: relation.updatedByName },
              {
                label: 'Updated',
                value: relation.updatedAt ? (
                  <time dateTime={toIso(relation.updatedAt)} className="font-mono">
                    {formatDateTime(relation.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <FamilyRelationModal
          open
          editing={relation}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
